import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Gamepad2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function AuthModal() {
  const { showAuth, setShowAuth, setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/register", { email, password, name });
        setUser(data);
        toast.success(`Welcome to the Mania, ${data.name}!`);
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        toast.success(`Welcome back, ${data.name}!`);
      }
      setShowAuth(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // "Continue with Google" pointed at auth.emergentagent.com, Emergent's
  // hosted OAuth. That is not ours and does not work off their platform, so
  // the button is gone rather than left to dead-end. Restoring Google
  // sign-in needs a real Google OAuth client plus a callback route.

  return (
    <Dialog open={showAuth} onOpenChange={setShowAuth}>
      <DialogContent className="bg-zinc-950 border-2 border-neon-cyan/40 text-white sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Gamepad2 className="w-7 h-7 text-neon-cyan" />
            <DialogTitle className="font-display text-2xl font-extrabold uppercase tracking-tight">
              {mode === "login" ? "Welcome Back" : "Join the Mania"}
            </DialogTitle>
          </div>
          <p className="text-sm text-zinc-400">
            {mode === "login" ? "Log in to keep playing and save your scores." : "Create a free account to unlock more games."}
          </p>
          <DialogDescription className="sr-only">
            {mode === "login" ? "Log in to your Video Game Mania account." : "Create a free Video Game Mania account."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-zinc-300">Display name</Label>
              <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                required className="bg-zinc-900 border-zinc-700 focus-visible:ring-neon-cyan" placeholder="PixelHero" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <Input id="email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              required className="bg-zinc-900 border-zinc-700 focus-visible:ring-neon-cyan" placeholder="you@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <Input id="password" type="password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
              required minLength={6} className="bg-zinc-900 border-zinc-700 focus-visible:ring-neon-cyan" placeholder="••••••••" />
          </div>

          {error && <p className="text-sm text-neon-pink" data-testid="auth-error">{error}</p>}

          <Button type="submit" data-testid="auth-submit-btn" disabled={loading}
            className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest rounded-none hover:bg-white transition-colors h-12">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "login" ? "Log In" : "Create Account"}
          </Button>
        </form>

        <p className="text-center text-sm text-zinc-400 mt-2">
          {mode === "login" ? "New here?" : "Already have an account?"}{" "}
          <button type="button" data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-neon-cyan font-semibold hover:underline">
            {mode === "login" ? "Sign up" : "Log in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
